import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Chat } from '@trustero/trustero-api-web/lib/chat/chat_pb'
import { PermalinkButton } from 'src/components/Reusable/Buttons/PermalinkButton'
import { TrusteroIntelligenceAbsoluteRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import palette from 'src/designSystem/variables/palette'
import {
  ShowPageTitleError,
  StyledEditRow,
  StyledNameInputContainer,
} from 'src/components/Reusable/ShowPage/Title/ShowPageTitle.styles'
import { ItemTitle } from 'src/components/PageLayout/ShowPage/BaseComponents'
import { EditOnClick } from 'src/components/Reusable/Inputs/EditOnClick'
import { StyledTextInput } from 'src/components/Reusable/Inputs/TextInput/styles'
import { useUser } from 'src/components/async/Users'
import { ChangelogButton } from 'src/components/Reusable/Buttons'
import {
  GatekeeperFlag,
  IsGatekeeperEnabled,
  useAgentChat,
  useHasPlaybooksUi,
} from 'src/context/Gatekeeper'
import { Tooltip } from 'src/components/Reusable/Tooltip'
import {
  ActionButtonGroup,
  Button,
  ButtonGroup,
} from 'src/components/Reusable/ActionButtonGroup'
import {
  AddSVG,
  ChatsSVG,
  ContextSVG,
  NewHistorySVG,
  PlaybookSVG,
  SearchSVG,
} from 'src/components/Icons/Basic'
import { TooltipIconButton } from 'src/components/Reusable/Buttons/TooltipIconButton'
import { useInfoPanelContext } from 'src/InfoPanel/InfoPanelContext'
import { useChatPermalink } from '../trusteroIntelligence.hooks'
import { usePanelVisibilityContext } from '../trusteroIntelligence.context'
import { ChatVisibility } from './ChatVisibility'
import { usePlaybookPanelContext } from './chatPanel.context'
import { ChatPermalinkSVG } from './chatPanel.styles'
import { ChatSearchPopup } from './ChatSearchPopup'

export const ChatPermalink = ({
  isPrivate,
  chatId,
  isAgentChat,
  artifactId,
}: {
  isPrivate?: boolean
  chatId: string
  isAgentChat?: boolean
  artifactId?: string
}): JSX.Element | null => {
  const permalink = useChatPermalink(chatId, artifactId)
  const tooltipBody = isPrivate
    ? 'This chat is private, make it public to share.'
    : ''

  return (
    <PermalinkButton
      permalinkUrl={permalink}
      isPermalink={true}
      placement="left"
      tooltipBody={tooltipBody}
      disabled={isPrivate}
      icon={isAgentChat ? <ChatPermalinkSVG /> : undefined}
      hoverBgColor={'transparent'}
      svgHoverBgColor={artifactId ? palette.neutral[200] : 'transparent'}
    />
  )
}

export const ChatNameInput = ({
  chatName,
  onUpdate,
  isReadOnly,
  ownerName,
}: {
  chatName: string
  onUpdate: (chatName: string) => void
  isReadOnly: boolean
  ownerName: string
}): JSX.Element => {
  const [tempName, setTempName] = useState(chatName)
  const [lastSaveDate, setLastSaveDate] = useState<Date | null | undefined>(
    null,
  )

  useEffect(() => {
    setTempName(chatName)
  }, [chatName])

  const onSave = useCallback(async (): Promise<void> => {
    const trimmedName = tempName.trim()
    if (trimmedName.length && trimmedName.length <= 256) {
      await onUpdate(trimmedName)
    }
  }, [onUpdate, tempName])

  if (isReadOnly) {
    return (
      <Tooltip
        id="chat-name-tooltip"
        trigger={['hover', 'focus']}
        tooltipBody={`Chat created by ${ownerName}. Title not editable.`}
        placement="bottom"
      >
        <ItemTitle>{chatName}</ItemTitle>
      </Tooltip>
    )
  }

  return (
    <EditOnClick
      isInline
      onSave={onSave}
      isEmpty={!tempName.length}
      editMode={
        <StyledEditRow>
          <StyledNameInputContainer>
            <StyledTextInput
              name={'chatName'}
              onChange={(e) => setTempName(e.target.value)}
              value={tempName}
              showError={false}
              style={{ minWidth: '400px' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  onSave()
                  setLastSaveDate(new Date())
                }
              }}
            />
            <ShowPageTitleError showError={!!getEditChatNameError(tempName)}>
              {getEditChatNameError(tempName)}
            </ShowPageTitleError>
          </StyledNameInputContainer>
        </StyledEditRow>
      }
      lastSaveDate={lastSaveDate}
    >
      <Tooltip
        id="chat-name-tooltip"
        trigger={['hover', 'focus']}
        tooltipBody={`Chat created by you.`}
        placement="bottom"
      >
        <ItemTitle>{chatName}</ItemTitle>
      </Tooltip>
    </EditOnClick>
  )
}

export const getEditChatNameError = (tempName: string): string => {
  const name = tempName.trim()
  return name.length > 256
    ? 'Title is too long'
    : !name.length
    ? 'Title is required'
    : ''
}

export type ChatPanelActionsProps = {
  isPrivate: boolean
  setIsPrivate: (isPrivate: boolean) => void
  chat?: Chat
  isLoading?: boolean
}

export const ChatPanelActions = ({
  isPrivate,
  setIsPrivate,
  chat,
  isLoading,
}: ChatPanelActionsProps): JSX.Element => {
  const showAgentChat = useAgentChat()
  const { setAgentPanel } = usePanelVisibilityContext()
  const { data: user } = useUser()
  const params = useParams()
  const { setInfoPanelState } = useInfoPanelContext()
  const canCreateChats = useCanPerformAction(CAPABILITY.CAN_CREATE_CHATS)
  // Chats and playbooks are independent domains, and the flow this opens ends at
  // playbook.Playbook/CreatePlaybook.
  const canCreatePlaybooks = useCanPerformAction(
    CAPABILITY.CAN_CREATE_PLAYBOOKS,
  )
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const hasPlaybooksUi = useHasPlaybooksUi()
  const { openCreatePlaybookPanel } = usePlaybookPanelContext()

  const setAgentPanelCallback = useCallback(
    (panel: string) => {
      setInfoPanelState({
        isShown: false,
        render: <div></div>,
      })
      setAgentPanel(panel)
    },
    [setAgentPanel, setInfoPanelState],
  )

  const handleSearchClick = useCallback(() => {
    setIsSearchOpen((prev) => !prev)
  }, [])

  const handleSearchClose = useCallback(() => {
    setIsSearchOpen(false)
  }, [])

  const groups = useMemo(() => {
    const globalButtons: Button[] = []

    if (showAgentChat) {
      if (canCreateChats) {
        globalButtons.push({
          id: 'add',
          button: (
            <TooltipIconButton
              tooltipText="New Chat"
              icon={
                <Link
                  to={`/${params.pageContext}/${TrusteroIntelligenceAbsoluteRoutes.INDEX}`}
                >
                  <AddSVG />
                </Link>
              }
            />
          ),
        })
      }

      globalButtons.push({
        id: 'chats',
        button: (
          <TooltipIconButton
            tooltipText="Chats"
            icon={
              <ChatsSVG onClick={() => setAgentPanelCallback('chathistory')} />
            }
          />
        ),
      })

      globalButtons.push({
        id: 'context',
        button: (
          <TooltipIconButton
            tooltipText="Chat Context"
            icon={
              <ContextSVG onClick={() => setAgentPanelCallback('context')} />
            }
          />
        ),
      })

      if (IsGatekeeperEnabled(GatekeeperFlag.CHAT_SEARCH)) {
        globalButtons.push({
          id: 'search',
          button: (
            <TooltipIconButton
              tooltipText="Search Chats"
              icon={<SearchSVG onClick={handleSearchClick} />}
            />
          ),
        })
      }

      if (canCreatePlaybooks && hasPlaybooksUi) {
        globalButtons.push({
          id: 'create-playbook',
          button: (
            <TooltipIconButton
              tooltipText="Create Playbook"
              icon={<PlaybookSVG onClick={openCreatePlaybookPanel} />}
            />
          ),
        })
      }
    }

    const localButtonGroup: Button[] = []

    if (chat?.getOwner()?.getId() === user?.getId()) {
      localButtonGroup.push({
        id: 'visibility',
        button: (
          <ChatVisibility
            isPrivate={isPrivate}
            setIsPrivate={setIsPrivate}
            chat={chat}
          />
        ),
      })
    }

    if (chat && !isLoading) {
      localButtonGroup.push({
        id: 'permalink',
        button: (
          <ChatPermalink
            isPrivate={isPrivate}
            chatId={chat.getId()}
            isAgentChat={showAgentChat}
          />
        ),
      })
      localButtonGroup.push({
        id: 'history',
        button: (
          <ChangelogButton
            subjectIds={[chat.getId()]}
            tooltipPlacement="left"
            icon={<NewHistorySVG />}
            onOpen={() => setAgentPanel(undefined)}
            subjectType={MODEL_TYPE.CHAT}
          />
        ),
      })
    }

    const groups: ButtonGroup[] = [{ id: 'global', buttons: globalButtons }]
    if (localButtonGroup.length > 0) {
      groups.push({ id: 'local', buttons: localButtonGroup })
    }

    return groups
  }, [
    showAgentChat,
    isLoading,
    isPrivate,
    setIsPrivate,
    user,
    chat,
    params.pageContext,
    setAgentPanel,
    setAgentPanelCallback,
    canCreateChats,
    canCreatePlaybooks,
    handleSearchClick,
    hasPlaybooksUi,
    openCreatePlaybookPanel,
  ])

  return (
    <>
      <ActionButtonGroup groups={groups} useNewStyle={showAgentChat} />
      <ChatSearchPopup isOpen={isSearchOpen} onClose={handleSearchClose} />
    </>
  )
}
